import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ErrorHandlingComponent} from '../../../error-handling.component';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BankAccount} from '../../../model/bank-account';
import {BankAccountService} from '../../../service/bank-account.service';
import {BankAccountDetailsByNameComponent} from '../bank-account-details-by-name/bank-account-details-by-name.component';
import {MatRadioChange} from '@angular/material';
import {BankAccountDetailsByIfscComponent} from '../bank-account-details-by-ifsc/bank-account-details-by-ifsc.component';
import {Validator} from '../../../utils/Validator';
import {Toaster, Type} from '../../../toaster/Toaster';

export enum BankNameSearchSource {
  name = 'name',
  ifsc = 'ifsc'
}

@Component({
  selector: 'app-update-bank-account',
  templateUrl: './update-bank-account.component.html',
  styleUrls: ['./update-bank-account.component.css']
})
export class UpdateBankAccountComponent  extends ErrorHandlingComponent implements OnInit {

  formGroup: FormGroup;
  isLoading: boolean;
  userId: string;
  bankAccount: BankAccount;
  accountTypes = [
    {value: 'saving', viewValue: 'Saving'},
    {value: 'current', viewValue: 'Current'}
  ];
  bankNameSearchSource: BankNameSearchSource;

  @ViewChild(BankAccountDetailsByIfscComponent)
  bankAccountDetailsByIfscComponent: BankAccountDetailsByIfscComponent;
  @ViewChild(BankAccountDetailsByNameComponent)
  bankAccountDetailsByNameComponent: BankAccountDetailsByNameComponent;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private dialogRef: MatDialogRef<UpdateBankAccountComponent>, formBuilder: FormBuilder,
              private validator: Validator, private bankAccountService: BankAccountService) {
    super();
    this.formGroup = formBuilder.group({
      accountHolderName: new FormControl(null, Validators.required),
      accountType: new FormControl(undefined, Validators.required),
      accountNumberGroup: formBuilder.group({
        accountNumber: new FormControl(null, [Validators.required, this.validator.numeric]),
        repeatedAccountNumber: new FormControl(null, [Validators.required, this.validator.numeric])
      }, {validator: UpdateBankAccountComponent.validateRepeatedAccountNumber}),
    });

  }
  private static validateRepeatedAccountNumber(control: AbstractControl): ValidationErrors | null {
    const accountNumberFormControl = control.get('accountNumber');
    const repeatedAccountNumberFormControl = control.get('repeatedAccountNumber');
    const originalValue = accountNumberFormControl.value;
    const repeatedValue = repeatedAccountNumberFormControl.value;
    if (repeatedValue !== originalValue) {
      return {match: true};
    } else {
      return null;
    }
  }


  ngOnInit() {
    this.userId = this.data.userId;
    this.resetBankAccount();
  }

  onSelectBankDetailsSearch(data: MatRadioChange) {
    this.resetBankDetails();
    this.bankNameSearchSource = data.value;
  }

  onDialogCancel() {
    this.resetBankAccount();
    this.closeDialog(null);
  }

  areBankDetailValid(): boolean {
    if (!this.bankAccount.bank_name || !this.bankAccount.branch_city || !this.bankAccount.branch_name || !this.bankAccount.ifsc) {
      return false;
    } else if (!this.bankAccountDetailsByIfscComponent && !this.bankAccountDetailsByNameComponent) {
      return false;
    } else if (this.bankAccountDetailsByIfscComponent && this.bankAccountDetailsByIfscComponent.searchBankFormGroup.valid) {
      return true;
    } else {
      return this.bankAccountDetailsByNameComponent && this.bankAccountDetailsByNameComponent.searchBankFormGroup.valid;
    }
  }

  async updateBankAccount(): Promise<void> {
    if (this.formGroup.valid && this.areBankDetailValid()) {
      try {
        this.isLoading = true;
        const createdBankAccount = await this.bankAccountService.create(this.bankAccount);
        Toaster.toast(`New bank account details updated`, Type.Success);
        this.closeDialog(createdBankAccount);
      } catch (error) {
        Toaster.toast(`Something went wrong while trying to save the bank account details`, Type.Error);
        this.closeDialog(null);
      } finally {
        this.resetBankAccount();
        this.isLoading = false;
      }
    } else {
      Toaster.toast(`Invalid bank account details`, Type.Error);
    }
  }

  private resetBankAccount() {
    this.bankAccount = new BankAccount();
    this.bankAccount.userId = this.userId;
  }

  private resetBankDetails() {
    this.bankAccount.bank_name = null;
    this.bankAccount.branch_city = null;
    this.bankAccount.branch_name = null;
    this.bankAccount.ifsc = null;
  }

  private closeDialog(bankAccount: BankAccount) {
    this.dialogRef.close({data: {bankAccount: bankAccount }});
  }

  protected errorMessages(): object {
    return {
      accountHolderName: [{
        type: 'required',
        message: `Please account holder's name`
      }],
      accountType: [{
        type: 'required',
        message: 'Please select the account type'
      }],
      accountNumber: [{
        type: 'required',
        message: 'Please provide a valid account number'
      }, {
        type: 'invalidNumber',
        message: 'Please enter numeric value'
      }],
      repeatedAccountNumber: [{
        type: 'required',
        message: 'Please provide a valid account number'
      }, {
        type: 'invalidNumber',
        message: 'Please enter numeric value'
      }],
      accountNumberGroup: [{
        type: 'match',
        message: 'Account Numbers do not match.'
      }]
    };
  }

}
